import React, { useState, useEffect } from 'react';
import { ui } from '../../../config/uiSizes';

interface EmailDomainParametersProps {
  parameters?: Record<string, unknown>;
  onParametersChange?: (parameters: Record<string, unknown>) => void;
}

export const EmailDomainParameters: React.FC<EmailDomainParametersProps> = ({
  parameters,
  onParametersChange
}) => {
  const [oldDomain, setOldDomain] = useState<string>((parameters?.['oldDomain'] as string) || '');
  const [newDomain, setNewDomain] = useState<string>((parameters?.['newDomain'] as string) || '');
  const [updatePrimaryEmail, setUpdatePrimaryEmail] = useState<boolean>(
    parameters?.['updatePrimaryEmail'] !== undefined
      ? (parameters['updatePrimaryEmail'] as boolean)
      : true
  );
  const [updateAlternateEmails, setUpdateAlternateEmails] = useState<boolean>(
    parameters?.['updateAlternateEmails'] !== undefined
      ? (parameters['updateAlternateEmails'] as boolean)
      : true
  );
  const [preservePrefix, setPreservePrefix] = useState<boolean>(
    parameters?.['preservePrefix'] !== undefined ? (parameters['preservePrefix'] as boolean) : true
  );

  useEffect(() => {
    if (onParametersChange) {
      onParametersChange({
        oldDomain,
        newDomain,
        updatePrimaryEmail,
        updateAlternateEmails,
        preservePrefix
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oldDomain, newDomain, updatePrimaryEmail, updateAlternateEmails, preservePrefix]);

  return (
    <div className="border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 p-6">
      <div className={`${ui.mb.title}`}>
        <h3 className={`font-bold text-gray-900 dark:text-gray-100 ${ui.text.sectionTitle}`}>Email Domain</h3>
        <p className={`text-gray-600 dark:text-gray-400 mt-1 ${ui.text.label}`}>
          Update email domains from old company domain to new company domain.
        </p>
      </div>

      <div className="space-y-4">
        {/* Old Domain */}
        <div>
          <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
            Old Domain <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={oldDomain}
            onChange={(e) => setOldDomain(e.target.value)}
            placeholder="@oldcompany.com"
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
        </div>

        {/* New Domain */}
        <div>
          <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
            New Domain <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            placeholder="@newcompany.com"
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
        </div>

        {/* Options */}
        <div className="space-y-3 pt-2">
          <div className={`flex items-center ${ui.gap.md}`}>
            <input
              type="checkbox"
              id="updatePrimaryEmail"
              checked={updatePrimaryEmail}
              onChange={(e) => setUpdatePrimaryEmail(e.target.checked)}
              className={`${ui.svg.sm} text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-400`}
            />
            <label
              htmlFor="updatePrimaryEmail"
              className={`${ui.text.label} dark:text-gray-300`}
            >
              Update primary email field
            </label>
          </div>

          <div className={`flex items-center ${ui.gap.md}`}>
            <input
              type="checkbox"
              id="updateAlternateEmails"
              checked={updateAlternateEmails}
              onChange={(e) => setUpdateAlternateEmails(e.target.checked)}
              className={`${ui.svg.sm} text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-400`}
            />
            <label
              htmlFor="updateAlternateEmails"
              className={`${ui.text.label} dark:text-gray-300`}
            >
              Update alternate email addresses
            </label>
          </div>

          <div className={`flex items-center ${ui.gap.md}`}>
            <input
              type="checkbox"
              id="preservePrefix"
              checked={preservePrefix}
              onChange={(e) => setPreservePrefix(e.target.checked)}
              className={`${ui.svg.sm} text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-400`}
            />
            <label htmlFor="preservePrefix" className={`${ui.text.label} dark:text-gray-300`}>
              Keep email username unchanged
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailDomainParameters;
